import styled, { css, keyframes } from "styled-components";

/*
 * Prodavnica, v2 redizajn (s.3): bez sidebara, sticky filter toolbar,
 * auto-fill grid, sve preko tokena.
 *
 * Animacije: intro naslova/toolbara na mount, chipovi stepenasto,
 * kartice per-ćelija na scroll (useReveal + RevealCell).
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

const labelIn = keyframes`
  from { opacity: 0; letter-spacing: 0.45em; }
  to { opacity: 1; letter-spacing: 0.15em; }
`;

export const Page = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 5rem;
`;

export const HeaderRow = styled.div`
  margin-bottom: 1.5rem;
`;

export const SectionLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
  animation: ${labelIn} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const PageH1 = styled.h1`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(40px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.08s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ResultCount = styled.span`
  color: var(--text-secondary);
  font-size: 0.95rem;
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ------------------------------------------------------------------ */
/* Sticky filter toolbar                                              */
/* ------------------------------------------------------------------ */
export const Toolbar = styled.div`
  position: sticky;
  /* Visina skupljenog headera (Header.styles.js): 54px desktop, 56px mobilno */
  top: 54px;
  z-index: 900;
  animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) 0.2s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  background: rgba(19, 16, 16, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
  margin: 0 -1.5rem 1.75rem;
  padding: 0.7rem 1.5rem;

  @media (max-width: 768px) {
    top: 56px;
  }
`;

export const ToolbarInner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
`;

/* Kategorija chipovi, horizontalni scroll na mobilnom */
export const ChipsRail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  overflow-x: auto;
  scrollbar-width: none;
  flex: 1;
  min-width: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  /* kategorije ulete jedna za drugom */
  & > * {
    animation: ${fadeUp} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
  }

  ${Array.from(
    { length: 12 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${250 + i * 40}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

export const CategoryChip = styled.button`
  flex-shrink: 0;
  height: 34px;
  padding: 0 0.9rem;
  border-radius: var(--radius-pill);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  background: ${(p) => (p.$active ? "var(--brand-dim)" : "transparent")};
  color: ${(p) => (p.$active ? "var(--brand)" : "var(--text-secondary)")};
  border: 1px solid
    ${(p) => (p.$active ? "rgba(255, 107, 53, 0.45)" : "var(--border-strong)")};

  &:hover {
    color: ${(p) => (p.$active ? "var(--brand)" : "var(--text-primary)")};
    border-color: ${(p) =>
      p.$active ? "rgba(255, 107, 53, 0.45)" : "var(--text-muted)"};
  }
`;

export const ToolbarControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;

  /* Na mobilnom kontrole žive u bottom-sheet-u iza "Filteri" dugmeta */
  @media (max-width: 900px) {
    display: none;
  }
`;

/* Plutajuće "Filteri" dugme, samo mobilno, fiksirano na dnu ekrana.
   Mora da živi VAN Toolbara (backdrop-filter pravi containing block). */
export const FiltersButton = styled.button`
  display: none;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  span.count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: var(--brand);
    color: #ffffff;
    border-radius: var(--radius-pill);
    font-size: 11px;
    font-weight: 700;
  }

  @media (max-width: 900px) {
    display: inline-flex;
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: calc(1.1rem + env(safe-area-inset-bottom));
    z-index: 1400;
    height: 44px;
    padding: 0 1.3rem;
    background: var(--bg-elevated);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-pill);
    color: var(--text-primary);
    font-size: 0.9rem;
    font-weight: 600;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);

    &:active {
      transform: translateX(-50%) scale(0.97);
    }
  }
`;

/* ------------------------------------------------------------------ */
/* Bottom-sheet (mobilni filteri)                                     */
/* ------------------------------------------------------------------ */
export const SheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2500;

  /* sheet postoji samo na mobilnom */
  @media (min-width: 901px) {
    display: none;
  }
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? "visible" : "hidden")};
  transition:
    opacity 0.25s ease,
    visibility 0.25s ease;
`;

export const Sheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2600;

  /* sheet postoji samo na mobilnom */
  @media (min-width: 901px) {
    display: none;
  }
  background: var(--bg-surface);
  border-top: 1px solid var(--border-strong);
  border-radius: 16px 16px 0 0;
  padding: 0.75rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom));
  transform: translateY(${(p) => (p.$open ? "0" : "100%")});
  transition: transform 0.28s ease;
  max-height: 80vh;
  overflow-y: auto;
`;

export const SheetHandle = styled.div`
  width: 42px;
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--border-strong);
  margin: 0 auto 1rem;
`;

export const SheetTitle = styled.div`
  font-family: var(--font-display);
  font-size: 1.1rem;
  text-transform: uppercase;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

export const SheetField = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    color: var(--text-muted);
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    margin-bottom: 0.45rem;
  }
`;

export const SheetApply = styled.button`
  width: 100%;
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 0.5rem;

  &:hover {
    background: var(--brand-hover);
  }
`;

export const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    color: var(--brand);
    pointer-events: none;
    z-index: 1;
  }
`;

export const SearchField = styled.input`
  height: 44px;
  width: 210px;
  box-sizing: border-box;
  padding: 0 0.9rem 0 2.4rem;
  background: var(--bg-app);
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.88rem;
  font-family: var(--font-body);
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--brand);
  }

  @media (max-width: 900px) {
    width: 100%;
    flex: 1;
  }
`;

/* Aktivni filteri (removable chipovi) */
export const ActiveFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: -0.75rem 0 1.5rem;
  animation: ${fadeUp} 0.35s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ActiveFilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 30px;
  padding: 0 0.75rem;
  border-radius: var(--radius-pill);
  background: var(--brand-dim);
  color: var(--brand);
  border: 1px solid rgba(255, 107, 53, 0.35);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    border-color: var(--brand);
  }
`;

export const ClearAllButton = styled.button`
  height: 30px;
  padding: 0 0.6rem;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
    text-decoration: underline;
  }
`;

/* ------------------------------------------------------------------ */
/* Grid + paginacija                                                  */
/* ------------------------------------------------------------------ */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

/* Ćelija grida sa scroll-reveal, wrapper oko deljene ProductCard komponente;
   flex rasteže karticu na punu visinu reda */
export const RevealCell = styled.div`
  display: flex;
  flex-direction: column;

  & > * {
    flex: 1;
  }

  opacity: 0;

  ${(p) =>
    p.$visible &&
    css`
      opacity: 1;
      animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      animation-delay: calc(var(--i, 0) * 60ms);
    `}

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

export const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) 0.25s backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 44px;
  padding: 0 1.5rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    border-color: var(--brand);
    color: var(--brand);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* Numerisana paginacija, fallback za crawlere, diskretna */
export const PageLinks = styled.nav`
  display: flex;
  justify-content: center;
  gap: 0.35rem;
  margin-top: 1.25rem;
  flex-wrap: wrap;

  a {
    min-width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.4rem;
    border-radius: var(--radius-control);
    color: var(--text-muted);
    font-size: 0.8rem;
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: var(--text-primary);
    }

    &.active {
      color: var(--brand);
      font-weight: 700;
    }
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 5rem 1rem;
  animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  svg {
    color: var(--text-muted);
    margin-bottom: 1rem;
  }

  p {
    color: var(--text-secondary);
    font-size: 1rem;
    margin: 0 0 1.5rem;
  }
`;
